import { useSession } from 'next-auth/react';
import useSWRImmutable from 'swr/immutable';

import { NEXT_AUTH_STATUSES } from '@/lib/constants';
import { TagsApiListResponse, TagsApiSingleResponse } from '@/types/api-types';
import { request } from '@/utils/api/request';
import { getTagsUrl } from '@/utils/helpers/api-urls';
import { trackUser } from '@/utils/tracking';

export const useTags = () => {
  const { status } = useSession();

  const getUrl = () => {
    if (status !== NEXT_AUTH_STATUSES.AUTHENTICATED) return null;
    return getTagsUrl();
  };

  const { isLoading, data, error, mutate } = useSWRImmutable<TagsApiListResponse>(getUrl(), request, {
    keepPreviousData: true,
  });

  const handleCreateTag = async ({ label }: { label: string }) => {
    const result = await request<TagsApiSingleResponse>(getTagsUrl(), { body: { label } }, 'POST');
    if (result.tag) {
      trackUser.event('Created tag');
      return { tags: [...(data?.tags || []), result.tag] };
    }

    return { tags: [...(data?.tags || [])] };
  };

  const handleDeleteTag = async (id: string) => {
    const result = await request(getTagsUrl(id), undefined, 'DELETE');
    if (result) {
      trackUser.event('Deleted tag');
    }

    return { tags: data?.tags?.filter((tag) => tag.id === id) || [] };
  };

  const createTag = async ({ label }: { label: string }) => {
    await mutate(handleCreateTag({ label }), {
      optimisticData: { tags: [...(data?.tags || []), { label }] },
    });
  };

  const deleteTag = async (id: string) => {
    await mutate(handleDeleteTag(id), {
      optimisticData: { tags: data?.tags?.filter((tag) => tag.id === id) || [] },
    });
  };

  return {
    isLoading,
    tags: data?.tags,
    error,
    createTag,
    deleteTag,
    mutate,
  };
};
